import { AdminPageLayout } from '@/components/admin/AdminPageLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { useAuth } from '@/main';
import { trpc } from '@/utils/trpc';
import { useSuperAdmin } from '@/utils/use-super-admin';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { format } from 'date-fns';
import { Download, FileText, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_protected/admin/qa-export')({
  component: QAExportPage,
});

function QAExportPage() {
  const { orgId } = useAuth();
  const { isSuperAdmin, isLoading: isAdminLoading } = useSuperAdmin();
  const [startDate, setStartDate] = useState<Date | undefined>(new Date());
  const [endDate, setEndDate] = useState<Date | undefined>(new Date());
  const router = useRouter();

  const startDateString = startDate ? format(startDate, 'yyyy-MM-dd') : '';
  const endDateString = endDate ? format(endDate, 'yyyy-MM-dd') : '';

  // Redirect non-super admins to dashboard
  useEffect(() => {
    if (!isAdminLoading && !isSuperAdmin) {
      router.navigate({ to: '/dashboard' });
    }
  }, [isAdminLoading, isSuperAdmin, router]);

  const exportQAReport = trpc.calls.exportQAReport.useMutation({
    onSuccess: (data) => {
      // Create and download the files
      const datePrefix =
        startDateString && endDateString
          ? startDateString === endDateString
            ? startDateString
            : `${startDateString}_to_${endDateString}`
          : 'qa-report';
      downloadCSVFile(data.mainCsv, `qa-report-${datePrefix}.csv`);
      downloadCSVFile(data.summaryCsv, `qa-report-summary-${datePrefix}.csv`);
      downloadCSVFile(data.actionsCsv, `qa-report-actions-${datePrefix}.csv`);
    },
    onError: (error) => {
      toast.error(`Export failed: ${error.message}`);
    },
  });

  const handleExport = () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates');
      return;
    }

    if (startDate > endDate) {
      alert('Start date must be before or equal to end date');
      return;
    }

    exportQAReport.mutate({
      startDate: startDateString,
      endDate: endDateString,
      timezone: 'America/New_York',
    });
  };

  // Helper function to download CSV content as file
  const downloadCSVFile = (csvContent: string, filename: string) => {
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  if (isAdminLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <AdminPageLayout
      title="QA Export"
      description="Export quality assurance reports for a date range. This will generate three CSV files: main report, summary, and actions required."
      icon={FileText}
      isSuperAdminPage={true}
    >
      <Card>
        <CardHeader>
          <CardTitle>Export Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Start Date</h3>
              <DatePicker
                date={startDate}
                onDateChange={setStartDate}
                placeholder="Pick start date"
                className="w-[240px]"
              />
            </div>

            <div className="space-y-2">
              <h3 className="text-sm font-medium">End Date</h3>
              <DatePicker
                date={endDate}
                onDateChange={setEndDate}
                placeholder="Pick end date"
                className="w-[240px]"
              />
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-medium">Timezone</h3>
            <p className="text-sm text-muted-foreground">America/New_York (Eastern Time)</p>
          </div>

          <Button
            onClick={handleExport}
            disabled={!startDate || !endDate || exportQAReport.isPending}
            className="w-full"
            size="lg"
          >
            {exportQAReport.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating Report...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Export QA Report
              </>
            )}
          </Button>

          {exportQAReport.error && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
              <strong>Error:</strong> {exportQAReport.error.message}
            </div>
          )}

          {exportQAReport.data && (
            <div className="text-sm text-green-600 bg-green-50 p-3 rounded-md">
              <strong>Success!</strong> Generated {exportQAReport.data.recordCount} records for{' '}
              {exportQAReport.data.dateRange}. Three CSV files have been downloaded to your
              computer.
            </div>
          )}
        </CardContent>
      </Card>
    </AdminPageLayout>
  );
}
